/**
 * HelpCenter — single-page support form.
 *
 * The submit handler does NOT call our backend. It composes a
 * `mailto:support@cinerian.com` URL with the form contents pre-filled
 * and hands off to the user's default mail client (per product
 * decision — option B, zero infra). This trades polish for speed:
 * no Resend account, no domain verification, no API key. Upgradeable
 * later to a backend + transactional email without changing the UX.
 *
 * Layout: a single hero + two-column body. Left = the form,
 * right = a "Reach us" card with mailto links. Below the columns we
 * surface a small FAQ-ish strip pointing users at the most common
 * destinations so they don't always need to write in.
 */

import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Send, LifeBuoy, MessageSquare, ArrowUpRight, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

const SUPPORT_EMAIL = 'support@cinerian.com';
const REACH_EMAIL   = 'reach@cinerian.com';

// Single source of truth for the dropdown. Adding a new purpose only
// requires extending this list.
const PURPOSES = [
  { value: 'feature_request',  label: 'Feature request' },
  { value: 'feature_help',     label: 'Help with a feature' },
  { value: 'billing',          label: 'Billing & cancellation' },
  { value: 'bug_report',       label: 'Bug report' },
  { value: 'account',          label: 'Account / login issue' },
  { value: 'other',            label: 'Something else' },
];

const purposeLabel = (value) =>
  PURPOSES.find((p) => p.value === value)?.label || 'Other';

// Short how-tos for the parts of FrameFlow that need a sentence of context.
// Kept here (not a CMS) so they ship with the feature that they describe.
const GUIDES = [
  {
    id: 'upload',
    q: 'Which script formats and languages can I upload?',
    a: 'PDF, Word (.docx), plain text, Final Draft (.fdx) and Fountain. FrameFlow reads standard INT./EXT. sluglines and Indian-style headings such as "Scene 12 – Sekhar house – Night", with scene words and digits in Hindi, Telugu, Tamil, Kannada, Malayalam, Bengali, Gujarati and Punjabi. Timecoded censor/continuity scripts work too. If a PDF was typeset in a legacy (non-Unicode) font, the readable parts are kept and you get a warning; "Import with AI" handles anything the rule parser misses.',
  },
  {
    id: 'revision',
    q: 'The script changed. How do I re-import without losing my work?',
    a: 'Upload the new draft as usual, then choose "Compare with existing scenes" instead of Import. FrameFlow matches scenes by number, then by heading, then by text, and shows what changed, what is new and what is gone. Updating a scene rewrites only its heading and text — shots, cast, status, schedule and the elements you added by hand stay. Scenes missing from the revision are listed but only deleted if you tick them. Renumbering is detected and can be adopted.',
  },
  {
    id: 'elements',
    q: 'Can I correct the AI breakdown?',
    a: 'Yes. On the AI Breakdown page, or on a scene, click Edit next to "Breakdown elements" to add, remove or rename items in any of the 14 categories (cast, extras, props, set dressing, wardrobe, makeup & hair, vehicles, animals, stunts, VFX, SFX, sound & music, special equipment, locations). Items you add are marked with a pencil and are never overwritten when you re-analyse the scene. Editing costs no AI credits.',
  },
  {
    id: 'stripboard',
    q: 'How do I build shoot days?',
    a: 'Open the film\'s Board and switch to the Stripboard view. "Auto-build days" groups the unscheduled scenes by location, then INT/EXT, then day/night, and splits them into days — preview it, set Day 1\'s date and a weekly off day, then build. Days you made by hand are never touched, and you can undo from the toast. Drag strips between days or drop one between two strips to set shooting order. Strip colours follow the industry convention: white INT/day, yellow EXT/day, blue INT/night, green EXT/night. The "Day out of days" toggle shows each cast member\'s work and hold days.',
  },
  {
    id: 'callsheet',
    q: 'How do call sheets work?',
    a: 'On the stripboard, click the clipboard icon on a day (or "Create call sheet" in its menu). FrameFlow drafts the sheet from the day: cast with their scenes, all crew grouped by department, calls, locations, and the address, hospital and key contacts from your previous sheet. Adjust individual call and pickup times, untick anyone who is off, add notes and the next-day advance, then Send. Everyone with an email address receives a private link; for everyone else, the Links panel gives you a link per person to forward on WhatsApp. Each person\'s link shows their own call time first and a Confirm button — the day\'s chip on the board shows how many have confirmed. Editing a sent sheet creates a new version; re-sending keeps confirmations.',
  },
  {
    id: 'coverage',
    q: 'How do I mark shots on set?',
    a: 'Open the day\'s clapperboard icon on the board, or go to the film\'s "On set" page, on your phone. Tap Shot, Covered or Pickup on each shot, type the take number and a note, and "Wrap scene" when it is done. Every tap saves immediately. If the network drops, your taps are kept on the phone and sent when it returns — the banner at the top shows how many are waiting. Add the page to your home screen for the fastest experience.',
  },
  {
    id: 'dpr',
    q: 'What is the daily production report and who can send it?',
    a: 'The DPR is assembled at wrap from the day\'s scenes and shots, the call sheet and tomorrow\'s schedule: scenes completed, shots covered, pickups owed, call and wrap slip, day length, delays, cast worked and notes. Open it from the day\'s menu on the board, type the actual times and any delays, and it is ready. Reading it works on every plan; sending it to your producer as an email with the PDF attached is a Studio feature.',
  },
  {
    id: 'exports',
    q: 'Why does my PDF have a FrameFlow footer?',
    a: 'On the Free plan every export — shot list, day breakdown, day-out-of-days, call sheet — and every crew link carries a small "Made with FrameFlow" footer. Director and Studio plans export clean copies. The footer is never a watermark over the content; the document is always usable as-is.',
  },
  {
    id: 'credits',
    q: 'What uses AI credits and what doesn\'t?',
    a: 'AI credits are spent only when Gemini reads your script: the AI script import, per-scene re-analysis, suggested shots and AI insights. Everything else — editing elements, building shoot days, the day-out-of-days, call sheets, coverage mode, daily reports, PDFs and revision re-import — is free of credits on every plan.',
  },
];


const HelpCenter = () => {
  const { user } = useAuth() || {};
  const [purpose, setPurpose] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isValid = useMemo(
    () => !!purpose && message.trim().length >= 10,
    [purpose, message],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);

    // Build the mailto. Include the sender identity (if available) as a
    // signature so the support inbox knows who wrote in even when the
    // user's "From" address differs from their FrameFlow login.
    const subject = `[FrameFlow Help] ${purposeLabel(purpose)}`;
    const signature = user?.email
      ? `\n\n— Sent from FrameFlow Help Center\nAccount: ${user.email}`
      : `\n\n— Sent from FrameFlow Help Center`;
    const body = `${message.trim()}${signature}`;

    const href =
      `mailto:${SUPPORT_EMAIL}` +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;

    // window.location.href triggers the OS mail-handler. We don't reset
    // the form here so the user can re-submit if their mail client
    // didn't catch the first hand-off (e.g. browser blocked it).
    window.location.href = href;

    toast.success('Opening your email app…', {
      description: `We'll route this to ${SUPPORT_EMAIL}.`,
      duration: 4000,
    });

    // Small grace period so the toast registers; then re-enable submit.
    setTimeout(() => setSubmitting(false), 1200);
  };

  return (
    <div className="bg-background flex-1 min-w-0" data-testid="help-center">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12 max-w-6xl">

        {/* Hero */}
        <header className="mb-10 md:mb-12 max-w-3xl">
          <p className="text-xs font-semibold tracking-[0.22em] text-primary uppercase mb-3">
            Help Center
          </p>
          <h1
            className="font-display font-bold tracking-tight text-4xl sm:text-5xl leading-[1.05] mb-4"
            data-testid="help-hero-title"
          >
            How can we help?
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Tell us what&rsquo;s on your mind and we&rsquo;ll get back to you over email.
            For everything else, reach us directly — links on the right.
          </p>
        </header>

        {/* Guides */}
        <section className="rounded-2xl border border-border bg-card p-6 md:p-8 mb-8" data-testid="help-guides">
          <div className="flex items-center gap-3 mb-4">
            <span className="size-10 rounded-xl bg-secondary flex items-center justify-center">
              <BookOpen className="size-5 text-primary" />
            </span>
            <div>
              <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">Guides</p>
              <h2 className="font-display text-lg font-semibold tracking-tight mt-0.5">The short answers</h2>
            </div>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {GUIDES.map((g) => (
              <AccordionItem key={g.id} value={g.id}>
                <AccordionTrigger className="text-left text-sm font-medium">{g.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{g.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Two-column body */}
        <div className="grid gap-8 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px]">

          {/* Form card */}
          <section
            className="rounded-2xl border border-border bg-card p-6 md:p-8"
            data-testid="help-form-card"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="size-10 rounded-xl bg-secondary flex items-center justify-center">
                <MessageSquare className="size-5 text-primary" />
              </span>
              <div>
                <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">
                  Get in touch
                </p>
                <h2 className="font-display text-xl font-semibold tracking-tight mt-0.5">
                  Send us a message
                </h2>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="help-form">
              {/* Purpose */}
              <div className="space-y-2">
                <Label
                  htmlFor="purpose"
                  className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground"
                >
                  Purpose
                </Label>
                <Select value={purpose} onValueChange={setPurpose}>
                  <SelectTrigger
                    id="purpose"
                    className="h-11 rounded-xl"
                    data-testid="help-purpose-select"
                  >
                    <SelectValue placeholder="What can we help with?" />
                  </SelectTrigger>
                  <SelectContent>
                    {PURPOSES.map((p) => (
                      <SelectItem
                        key={p.value}
                        value={p.value}
                        data-testid={`help-purpose-option-${p.value}`}
                      >
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label
                  htmlFor="message"
                  className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground"
                >
                  Tell us more
                </Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Share as much detail as you can — what you're trying to do, what you expected, what happened instead, any error messages…"
                  rows={8}
                  className="rounded-xl resize-y min-h-[180px]"
                  data-testid="help-message-textarea"
                />
                <p className="text-xs text-muted-foreground">
                  Minimum 10 characters. {message.trim().length > 0 && (
                    <span className="font-medium text-foreground/70">
                      {message.trim().length} entered
                    </span>
                  )}
                </p>
              </div>

              {/* Submit */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={!isValid || submitting}
                  className="rounded-xl h-11 px-5"
                  data-testid="help-submit-button"
                >
                  <Send className="size-4 mr-1.5" />
                  {submitting ? 'Opening your email…' : 'Send message'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Opens in your email app, addressed to {SUPPORT_EMAIL}.
                </p>
              </div>
            </form>
          </section>

          {/* Reach-us side card */}
          <aside className="space-y-6">
            <section
              className="rounded-2xl border border-border bg-card p-6"
              data-testid="help-reach-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="size-10 rounded-xl bg-secondary flex items-center justify-center">
                  <Mail className="size-5 text-primary" />
                </span>
                <div>
                  <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">
                    Reach us at
                  </p>
                  <h3 className="font-display text-lg font-semibold tracking-tight mt-0.5">
                    Email us directly
                  </h3>
                </div>
              </div>
              <a
                href={`mailto:${REACH_EMAIL}`}
                className="group inline-flex items-center gap-1.5 font-mono text-sm font-medium text-primary hover:text-primary/80 transition-colors break-all"
                data-testid="help-reach-email-link"
              >
                {REACH_EMAIL}
                <ArrowUpRight className="size-3.5 opacity-60 group-hover:opacity-100 transition-opacity" />
              </a>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                For partnerships, press, and anything that doesn&rsquo;t fit the
                support form.
              </p>
            </section>

            <section
              className="rounded-2xl border border-border bg-card p-6"
              data-testid="help-support-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="size-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <LifeBuoy className="size-5 text-primary" />
                </span>
                <div>
                  <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">
                    Support
                  </p>
                  <h3 className="font-display text-lg font-semibold tracking-tight mt-0.5">
                    Direct line
                  </h3>
                </div>
              </div>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="group inline-flex items-center gap-1.5 font-mono text-sm font-medium text-primary hover:text-primary/80 transition-colors break-all"
                data-testid="help-support-email-link"
              >
                {SUPPORT_EMAIL}
                <ArrowUpRight className="size-3.5 opacity-60 group-hover:opacity-100 transition-opacity" />
              </a>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                The form above lands here. Typical response time: 1&ndash;2
                business days.
              </p>
            </section>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default HelpCenter;
